import { DocumentTypes, ManualAlignZoneCustomData, SerializedManualAlignZone } from 'types';

const CORNER_HANDLE_SIZE = 10;
const EDGE_HANDLE_LENGTH = 22;
const EDGE_HANDLE_THICKNESS = 10;

export interface ZoneRect {
  X: number;
  Y: number;
  Width: number;
  Height: number;
}

export interface ResizeHandlePoint {
  x: number;
  y: number;
}

interface ManualAlignAnnotationSource {
  Id: string;
  PageNumber: number;
  getRect(): { x1: number; y1: number; x2: number; y2: number };
  getCustomData(key: string): string;
}

/** Corner handles, plus the four edge midpoints while the aspect ratio is unlocked. */
export function manualAlignResizeHandlePoints(zone: ZoneRect, isUnlocked: boolean): ResizeHandlePoint[] {
  const { X, Y, Width, Height } = zone;
  const corners = [
    { x: X, y: Y },
    { x: X + Width, y: Y },
    { x: X, y: Y + Height },
    { x: X + Width, y: Y + Height },
  ];
  if (!isUnlocked) return corners;
  return [
    ...corners,
    { x: X + Width / 2, y: Y },
    { x: X + Width / 2, y: Y + Height },
    { x: X, y: Y + Height / 2 },
    { x: X + Width, y: Y + Height / 2 },
  ];
}

export function cornerHandleSize(): number {
  return CORNER_HANDLE_SIZE;
}

export interface HandleRect {
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  getWidth?: () => number;
  getHeight?: () => number;
}

/** Bounds of a side handle from PDFTron's public rect (`x1`/`y1` plus `getWidth`/`getHeight` or `x2`/`y2`). */
export function capsuleBoundsFromHandleRect(
  dimensions: HandleRect,
): { x: number; y: number; width: number; height: number } | undefined {
  const { x1, y1 } = dimensions;
  if (typeof x1 !== 'number' || typeof y1 !== 'number') return undefined;

  const widthFromGetter = dimensions.getWidth?.();
  const heightFromGetter = dimensions.getHeight?.();
  const width = widthFromGetter ?? (typeof dimensions.x2 === 'number' ? dimensions.x2 - x1 : undefined);
  const height = heightFromGetter ?? (typeof dimensions.y2 === 'number' ? dimensions.y2 - y1 : undefined);
  if (typeof width !== 'number' || typeof height !== 'number' || width <= 0 || height <= 0) return undefined;

  return { x: x1, y: y1, width, height };
}

/** Horizontal capsule for the top and bottom edges, vertical capsule for the left and right edges. */
export function edgeHandleSize(handle: 'width-edge' | 'height-edge'): { width: number; height: number } {
  if (handle === 'height-edge') {
    return { width: EDGE_HANDLE_LENGTH, height: EDGE_HANDLE_THICKNESS };
  }
  return { width: EDGE_HANDLE_THICKNESS, height: EDGE_HANDLE_LENGTH };
}

/** Custom-data value written when a reloaded overlay was saved unlocked. Absent when locked. */
export function aspectRatioUnlockedDataValue(isUnlocked: boolean | undefined): 'true' | undefined {
  return isUnlocked ? 'true' : undefined;
}

/** Segment event for the aspect-ratio toggle. Unlocking and locking share the same payload. */
export function aspectRatioToggleTrackEvent(
  isUnlocking: boolean,
  inspectionId: string | undefined,
  zoneNumberStr: string,
): {
  name: 'aspect-ratio-unlocked' | 'aspect-ratio-locked';
  data: { inspectionId: string | undefined; zoneNumber: number | undefined };
} {
  return {
    name: isUnlocking ? 'aspect-ratio-unlocked' : 'aspect-ratio-locked',
    data: {
      inspectionId,
      zoneNumber: zoneNumberStr ? parseInt(zoneNumberStr, 10) : undefined,
    },
  };
}

export function serializeManualAlignZone(annotation: ManualAlignAnnotationSource): SerializedManualAlignZone {
  const rect = annotation.getRect();
  let anchorPoint = { x: 0, y: 0 };
  const anchorPointData = annotation.getCustomData('anchorPoint');
  try {
    if (anchorPointData && anchorPointData !== 'none') {
      anchorPoint = JSON.parse(anchorPointData);
    }
  } catch {
    console.error('Error parsing anchor point data', anchorPointData);
  }
  const originZoneDocumentType = annotation.getCustomData(ManualAlignZoneCustomData.originZoneDocumentType) as
    | DocumentTypes
    | undefined;
  const zoneNumberStr = annotation.getCustomData(ManualAlignZoneCustomData.zoneNumber);
  const zoneNumber = zoneNumberStr ? parseInt(zoneNumberStr, 10) : undefined;
  const resized = annotation.getCustomData(ManualAlignZoneCustomData.resized) === 'true' || undefined;
  const aspectRatioUnlocked =
    annotation.getCustomData(ManualAlignZoneCustomData.aspectRatioUnlocked) === 'true' || undefined;
  return {
    annotationId: annotation.Id,
    pageNumber: annotation.PageNumber,
    x1: rect.x1,
    y1: rect.y1,
    x2: rect.x2,
    y2: rect.y2,
    anchorPoint,
    linkedZoneId: annotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId) || '',
    ...(originZoneDocumentType && { originZoneDocumentType }),
    ...(zoneNumber != null && { zoneNumber }),
    ...(resized && { resized }),
    ...(aspectRatioUnlocked && { aspectRatioUnlocked }),
  };
}
